import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ChevronLeft, ChevronDown, ChevronRight, Sparkles, Check, X, Plus, Trash2,
  FileText, Loader2, TrendingUp, Clapperboard, Info, Swords, ArrowRight, Pencil,
} from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import useApi from "../../hooks/useApi";
import Speedometer from "../../components/Speedometer";
import EvidenceDrawer from "../../components/sales/EvidenceDrawer";
import DataStatusBadge from "../../components/sales/DataStatusBadge";
import { scoreColor, COMPONENT_ICON, MIX_META } from "../../lib/sales";

const KIND_META = {
  strength: { label: "Strength", color: "#5F8213" },
  weakness: { label: "Weakness", color: "#C0473F" },
  opportunity: { label: "Opportunity", color: "#2F6BFF" },
  observation: { label: "Observation", color: "#6B6E62" },
};

export default function HealthCheckResult() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data, loading, refetch } = useApi(`/sales/health-checks/${id}`, [id]);
  const [active, setActive] = useState(null);
  const [obs, setObs] = useState([]);
  const [manual, setManual] = useState([]);
  const [manualText, setManualText] = useState("");
  const [showMethod, setShowMethod] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [comp, setComp] = useState({ agency: "", branch: "" });

  useEffect(() => {
    if (data) { setObs(data.ai_observations || []); setManual(data.manual_observations || []); }
  }, [data]);

  if (loading) return <div className="p-10 text-ink-3">Loading…</div>;
  if (!data) return <div className="p-10 text-ink-3">Not found.</div>;

  if (!data.components || data.components.length === 0) {
    return (
      <div className="p-8 max-w-xl">
        <Link to="/sales" className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink mb-6"><ChevronLeft size={16} /> Sales Director</Link>
        <div className="card p-10 text-center">
          <p className="font-semibold text-ink">This prospect hasn't been analysed yet.</p>
          <button onClick={() => navigate(`/sales/check/${id}/analysis`)} className="btn-primary focus-ring mt-5 mx-auto">Run Social Health Check <ArrowRight size={16} /></button>
        </div>
      </div>
    );
  }

  const { prospect, overall_score, band, potential, opportunities, nichecom, components, content_mix, competitors } = data;
  const sc = scoreColor(overall_score);
  const headline = obs.find((o) => o.included);

  async function saveReview() {
    try { await api.patch(`/sales/health-checks/${id}/review`, { ai_observations: obs, manual_observations: manual }); toast.success("Review saved"); refetch(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }
  async function addManual() {
    if (!manualText.trim()) return;
    try { const r = await api.post(`/sales/health-checks/${id}/manual-observation`, { text: manualText.trim() }); setManual((m) => [...m, r.data]); setManualText(""); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }
  async function addCompetitor() {
    if (!comp.agency) { toast.error("Enter a competitor agency name."); return; }
    const list = [...(competitors || []).map((c) => ({ agency: c.agency, branch: c.branch, website: c.website, socials: c.socials })), comp];
    try { await api.post(`/sales/health-checks/${id}/competitors`, { competitors: list }); setComp({ agency: "", branch: "" }); refetch(); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); }
  }
  async function generateReport() {
    setGenerating(true);
    try { await api.patch(`/sales/health-checks/${id}/review`, { ai_observations: obs, manual_observations: manual }); await api.post(`/sales/health-checks/${id}/report`); navigate(`/sales/check/${id}/report`); }
    catch (e) { toast.error(apiError(e?.response?.data?.detail)); setGenerating(false); }
  }

  return (
    <div className="pb-28">
      <header className="sticky top-0 z-20 flex items-center gap-4 px-6 sm:px-8 py-4 border-b backdrop-blur-md" style={{ background: "rgba(247,248,246,.85)", borderColor: "#E7E8E4" }}>
        <Link to="/sales" className="inline-flex items-center gap-1.5 text-sm text-ink-2 hover:text-ink"><ChevronLeft size={16} /> Sales Director</Link>
        <div className="ml-2 min-w-0">
          <h1 className="text-lg font-bold tracking-tight text-ink truncate">{prospect.company}</h1>
          <p className="text-sm text-ink-2 truncate">{prospect.branch}{prospect.postcode ? ` · ${prospect.postcode}` : ""}</p>
        </div>
        <button onClick={generateReport} disabled={generating} className="ml-auto btn-secondary focus-ring" data-testid="gen-report-top">
          {generating ? <Loader2 size={15} className="animate-spin" /> : <FileText size={15} />} Generate report
        </button>
      </header>

      <div className="px-6 sm:px-8 py-8 max-w-5xl space-y-8">
        {/* SCORE HERO */}
        <div className="rounded-2xl p-8 relative overflow-hidden" style={{ background: "var(--grad-ink)" }} data-testid="score-hero">
          <div className="blob" style={{ width: 420, height: 420, top: -160, left: -100, background: `radial-gradient(circle, ${overall_score >= 55 ? "rgba(147,192,32,.28)" : "rgba(192,71,63,.22)"}, transparent 70%)` }} />
          <div className="relative z-10 flex flex-col md:flex-row items-center gap-8">
            <Speedometer value={overall_score} size={280} dark label="Social Health Score" duration={1600} />
            <div className="flex-1 text-center md:text-left">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "#B6E14F" }}>Social Health Score</div>
              <div className="text-3xl font-extrabold text-white mt-2">{band} — {overall_score}/100</div>
              {headline && (
                <div className="mt-4 rounded-xl p-4 text-left" style={{ background: "rgba(255,255,255,.06)", border: "1px solid rgba(255,255,255,.1)" }}>
                  <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider" style={{ color: "#B6E14F" }}><Sparkles size={13} /> Growth observation</div>
                  <p className="text-sm mt-1.5 leading-relaxed" style={{ color: "rgba(255,255,255,.85)" }}>{headline.text}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* COMPONENT SCORES */}
        <section>
          <div className="flex items-center justify-between mb-3">
            <h2 className="text-base font-bold text-ink">Score breakdown</h2>
            <span className="text-xs text-ink-3">Click any score for the evidence</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {components.map((c) => {
              const cc = scoreColor(c.score); const Icon = COMPONENT_ICON[c.key];
              return (
                <button key={c.key} onClick={() => setActive(c)} data-testid={`component-${c.key}`} className="card card-hover p-5 text-left">
                  <div className="flex items-center justify-between">
                    <span className="grid place-items-center w-9 h-9 rounded-lg" style={{ background: cc.bg }}>{Icon && <Icon size={17} strokeWidth={1.75} style={{ color: cc.fg }} />}</span>
                    <div className="text-2xl font-extrabold tabular-nums" style={{ color: cc.fg }}>{c.score}</div>
                  </div>
                  <div className="text-sm font-semibold text-ink mt-3">{c.name}</div>
                  <div className="flex items-center justify-between mt-2">
                    <span className="text-xs font-medium" style={{ color: cc.fg }}>{c.band}</span>
                    <span className="inline-flex items-center gap-0.5 text-xs text-ink-3">Evidence <ChevronRight size={13} /></span>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full overflow-hidden" style={{ background: "#EDEEE9" }}>
                    <div className="h-full rounded-full" style={{ width: `${c.score}%`, background: cc.fg }} />
                  </div>
                </button>
              );
            })}
          </div>

          {/* Content mix */}
          {content_mix && (
            <div className="card p-5 mt-3" data-testid="content-mix">
              <div className="flex items-center justify-between mb-3">
                <span className="label-caps">Content mix (last 90 days)</span>
                <DataStatusBadge status="DEMO DATA" />
              </div>
              <div className="flex h-4 rounded-full overflow-hidden">
                {Object.entries(content_mix).filter(([, v]) => v > 0).map(([k, v]) => (
                  <div key={k} title={`${MIX_META[k]?.label}: ${v}%`} style={{ width: `${v}%`, background: MIX_META[k]?.color || "#ccc" }} />
                ))}
              </div>
              <div className="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
                {Object.entries(content_mix).filter(([, v]) => v > 0).map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1.5 text-xs text-ink-2">
                    <span className="w-2 h-2 rounded-full" style={{ background: MIX_META[k]?.color }} /> {MIX_META[k]?.label} {v}%
                  </span>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* BIGGEST OPPORTUNITIES */}
        {opportunities?.length > 0 && (
          <section data-testid="opportunities">
            <h2 className="text-base font-bold text-ink mb-3">Your biggest growth opportunities</h2>
            <div className="space-y-3">
              {opportunities.map((o) => (
                <div key={o.rank} className="card p-6 flex gap-5">
                  <div className="text-3xl font-extrabold tabular-nums shrink-0" style={{ color: "#93C020" }}>{String(o.rank).padStart(2, "0")}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-bold text-ink uppercase tracking-tight">{o.title}</span>
                      <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5" style={{ background: "rgba(147,192,32,.14)", color: "#5F8213" }}>+{o.point_gain} pts potential</span>
                    </div>
                    <p className="text-sm text-ink-2 mt-1.5 leading-relaxed">{o.body}</p>
                    <div className="flex items-center gap-2 mt-3 text-xs">
                      <span className="rounded-md px-2 py-1 font-semibold tabular-nums" style={{ background: scoreColor(o.current_score).bg, color: scoreColor(o.current_score).fg }}>{o.current_score}</span>
                      <ArrowRight size={13} className="text-ink-3" />
                      <span className="rounded-md px-2 py-1 font-semibold tabular-nums" style={{ background: "rgba(147,192,32,.14)", color: "#5F8213" }}>{o.projected_score}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* CURRENT VS POTENTIAL */}
        {potential && (
          <section className="rounded-2xl p-8" style={{ background: "var(--grad-ink)" }} data-testid="current-vs-potential">
            <div className="text-center">
              <div className="text-[11px] font-bold uppercase tracking-[0.18em]" style={{ color: "#B6E14F" }}>The opportunity</div>
              <h2 className="text-2xl font-extrabold text-white mt-1">Current vs potential Social Health</h2>
            </div>
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8 mt-6">
              <Speedometer value={potential.current} size={230} dark label="Current" duration={1400} />
              <div className="text-center">
                <div className="text-5xl font-extrabold" style={{ color: "#B6E14F" }}>+{potential.delta}</div>
                <div className="text-[11px] font-bold uppercase tracking-wider" style={{ color: "rgba(255,255,255,.55)" }}>Point opportunity</div>
              </div>
              <Speedometer value={potential.overall} size={230} dark label="Growth potential" duration={1400} />
            </div>
            <div className="mt-4 max-w-2xl mx-auto">
              <button onClick={() => setShowMethod((v) => !v)} data-testid="method-toggle" className="mx-auto flex items-center gap-1.5 text-sm font-medium" style={{ color: "rgba(255,255,255,.7)" }}>
                <Info size={14} /> How was potential calculated? <ChevronDown size={14} className={showMethod ? "rotate-180 transition-transform" : "transition-transform"} />
              </button>
              {showMethod && (
                <div className="mt-4 rounded-xl p-5" style={{ background: "rgba(255,255,255,.05)", border: "1px solid rgba(255,255,255,.1)" }}>
                  <p className="text-sm leading-relaxed" style={{ color: "rgba(255,255,255,.75)" }}>{potential.method}</p>
                  <div className="mt-4 divide-y" style={{ borderColor: "rgba(255,255,255,.08)" }}>
                    {potential.breakdown.map((b) => (
                      <div key={b.key} className="flex items-center justify-between py-2 text-sm">
                        <span style={{ color: "rgba(255,255,255,.8)" }}>{b.name} <span className="text-xs" style={{ color: "rgba(255,255,255,.4)" }}>({b.weight}%)</span></span>
                        <span className="tabular-nums" style={{ color: "rgba(255,255,255,.6)" }}>{b.current} → <b style={{ color: b.projected > b.current ? "#B6E14F" : "rgba(255,255,255,.6)" }}>{b.projected}</b></span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>
        )}

        {/* NICHECOM ADVANTAGE */}
        {nichecom && (
          <section className="card p-6 sm:p-8" style={{ border: "1px solid rgba(147,192,32,.35)" }} data-testid="nichecom-advantage">
            <div className="flex items-center gap-2.5 mb-4">
              <span className="grid place-items-center w-9 h-9 rounded-lg" style={{ background: "var(--grad-green-soft)" }}><Clapperboard size={17} strokeWidth={1.75} style={{ color: "#5F8213" }} /></span>
              <div><div className="label-caps" style={{ color: "#5F8213" }}>NicheCom content opportunity</div><div className="text-base font-bold text-ink">The content is already made — it just isn't being used</div></div>
              <DataStatusBadge status="VERIFIED" className="ml-auto" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              {[["Produced", nichecom.produced], ["Used socially", nichecom.used], ["Sitting unused", nichecom.missed]].map(([l, v], i) => (
                <div key={l} className="rounded-xl p-5 text-center" style={{ background: i === 2 ? "rgba(192,71,63,.08)" : "#F7F8F6" }}>
                  <div className="text-3xl font-extrabold tabular-nums" style={{ color: i === 2 ? "#C0473F" : "#16180F" }}>{v}</div>
                  <div className="label-caps mt-1">{l}</div>
                </div>
              ))}
            </div>
            <p className="text-sm text-ink-2 mt-4">Only <b>{nichecom.utilisation}%</b> of professionally produced NicheCom assets appear to have been used socially. That's <b>{nichecom.missed} ready-made pieces</b> of quality content available immediately.</p>
          </section>
        )}

        {/* COMPETITORS */}
        <section data-testid="competitors">
          <div className="flex items-center gap-2.5 mb-3">
            <Swords size={18} className="text-ink-2" />
            <h2 className="text-base font-bold text-ink">You vs local competitors</h2>
          </div>
          {competitors?.length > 0 && (
            <div className="card overflow-hidden mb-3">
              <div className="grid grid-cols-4 gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider text-ink-3 border-b" style={{ borderColor: "#E7E8E4" }}>
                <span>Metric</span><span className="text-center">You</span>
                {competitors.map((c) => <span key={c.id} className="text-center truncate">{c.agency}</span>)}
              </div>
              {[["Overall score", overall_score, (c) => c.score, ""], ["Posts / week", data.metrics.find((m) => m.key === "per_week")?.value, (c) => c.metrics.per_week.value, ""],
                ["Video %", data.metrics.find((m) => m.key === "video_pct")?.value, (c) => c.metrics.video_pct.value, "%"], ["Engagement %", data.metrics.find((m) => m.key === "engagement_rate")?.value, (c) => c.metrics.engagement.value, "%"]].map(([label, you, get, unit]) => (
                <div key={label} className="grid grid-cols-4 gap-2 px-4 py-3 text-sm border-b last:border-0" style={{ borderColor: "#F0F1EC" }}>
                  <span className="text-ink-2">{label}</span>
                  <span className="text-center font-bold tabular-nums text-ink">{you}{unit}</span>
                  {competitors.map((c) => <span key={c.id} className="text-center tabular-nums text-ink-2">{get(c)}{unit}</span>)}
                </div>
              ))}
              <div className="px-4 py-2 text-[11px] text-ink-3 flex items-center gap-1.5"><Info size={12} /> Competitor figures are labelled demo data — like-for-like only.</div>
            </div>
          )}
          {(competitors?.length || 0) < 3 && (
            <div className="card p-4 flex flex-col sm:flex-row gap-2">
              <input value={comp.agency} onChange={(e) => setComp((c) => ({ ...c, agency: e.target.value }))} placeholder="Competitor agency" data-testid="comp-agency" className="flex-1 rounded-xl border px-3 py-2 text-sm focus-ring" style={{ borderColor: "#E7E8E4" }} />
              <input value={comp.branch} onChange={(e) => setComp((c) => ({ ...c, branch: e.target.value }))} placeholder="Branch (optional)" data-testid="comp-branch" className="flex-1 rounded-xl border px-3 py-2 text-sm focus-ring" style={{ borderColor: "#E7E8E4" }} />
              <button onClick={addCompetitor} data-testid="add-competitor" className="btn-secondary focus-ring"><Plus size={15} /> Add competitor</button>
            </div>
          )}
        </section>

        {/* REVIEW ANALYSIS */}
        <section className="card p-6 sm:p-8" data-testid="review-analysis">
          <div className="flex items-center justify-between mb-4">
            <div><div className="label-caps">Internal — before you generate the report</div><h2 className="text-base font-bold text-ink mt-0.5">Review analysis</h2></div>
            <button onClick={saveReview} className="btn-secondary focus-ring" data-testid="save-review"><Check size={15} /> Save review</button>
          </div>
          <div className="space-y-2.5">
            {obs.map((o, i) => {
              const km = KIND_META[o.kind] || KIND_META.observation;
              return (
                <div key={o.id} className="rounded-xl p-4 flex items-start gap-3" style={{ background: o.included ? "#F7F8F6" : "rgba(0,0,0,.02)", opacity: o.included ? 1 : 0.55 }} data-testid={`obs-${i}`}>
                  <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 mt-0.5 shrink-0" style={{ color: km.color, background: `${km.color}1a` }}>{km.label}</span>
                  <p className="flex-1 text-sm text-ink leading-relaxed">{o.text}</p>
                  <button onClick={() => setObs((arr) => arr.map((x, j) => j === i ? { ...x, included: !x.included } : x))} data-testid={`obs-toggle-${i}`} className="shrink-0 grid place-items-center w-7 h-7 rounded-lg hover:bg-black/5" title={o.included ? "Exclude" : "Include"}>
                    {o.included ? <X size={15} className="text-ink-3" /> : <Plus size={15} className="text-ink-3" />}
                  </button>
                </div>
              );
            })}
            {obs.length === 0 && <p className="text-sm text-ink-3">No AI observations were generated.</p>}
            {manual.map((m, i) => (
              <div key={m.id} className="rounded-xl p-4 flex items-start gap-3" style={{ background: "rgba(184,137,43,.06)" }} data-testid={`manual-${i}`}>
                <span className="text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5 mt-0.5 shrink-0" style={{ color: "#B8892B", background: "rgba(184,137,43,.14)" }}>Manual</span>
                <p className="flex-1 text-sm text-ink leading-relaxed">{m.text}</p>
                <button onClick={() => setManual((arr) => arr.filter((_, j) => j !== i))} className="shrink-0 grid place-items-center w-7 h-7 rounded-lg hover:bg-black/5"><Trash2 size={14} className="text-ink-3" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-4">
            <input value={manualText} onChange={(e) => setManualText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addManual()} placeholder="Add a manual observation…" data-testid="manual-input" className="flex-1 rounded-xl border px-3 py-2 text-sm focus-ring" style={{ borderColor: "#E7E8E4" }} />
            <button onClick={addManual} className="btn-secondary focus-ring"><Pencil size={14} /> Add</button>
          </div>
        </section>
      </div>

      {/* Sticky generate */}
      <div className="fixed bottom-0 left-64 right-0 border-t px-8 py-4 flex items-center justify-between" style={{ background: "rgba(247,248,246,.9)", backdropFilter: "blur(8px)", borderColor: "#E7E8E4" }}>
        <div className="flex items-center gap-2 text-sm text-ink-2"><TrendingUp size={16} style={{ color: "#5F8213" }} /> Current <b className="text-ink">{overall_score}</b> → Potential <b style={{ color: "#5F8213" }}>{potential?.overall}</b></div>
        <button onClick={generateReport} disabled={generating} data-testid="generate-report" className="btn-primary focus-ring">
          {generating ? <><Loader2 size={16} className="animate-spin" /> Generating…</> : <>Generate Health Check report <ArrowRight size={16} strokeWidth={2} /></>}
        </button>
      </div>

      <EvidenceDrawer component={active} onClose={() => setActive(null)} />
    </div>
  );
}
